import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    Drawer, List, ListItem, ListItemIcon, ListItemText, Box, Divider, Button, IconButton,
    Typography
} from '@mui/material';
import { FaHome, FaUsers, FaChartBar, FaBox, FaCog, FaSignOutAlt, FaChevronLeft, FaChevronRight, FaEnvelope } from 'react-icons/fa';
import { TfiLayoutSliderAlt } from 'react-icons/tfi';
import { MdOutlineCategory } from 'react-icons/md';
import { GrGallery } from 'react-icons/gr';
import { motion } from 'framer-motion';
import { TbCategory2 } from "react-icons/tb";


const sidebarVariants = {
    open: { width: 300, transition: { duration: 0.3, ease: 'easeInOut' } },
    closed: { width: 90, transition: { duration: 0.3, ease: 'easeInOut' } },
};


const listVariants = {
    open: {
        transition: {
            staggerChildren: 0.07, 
            delayChildren: 0.2,    
        },
    },
    closed: {
        transition: {
            staggerChildren: 0.05, 
            staggerDirection: -1,  
        },
    },
};

const listItemVariants = {
    open: { opacity: 1, x: 0, transition: { duration: 0.3 } },
    closed: { opacity: 1, x: 10, transition: { duration: 0.2 } }, 
};


const buttonVariants = {
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95 },
};

const Sidebar = ({ isOpen, toggleSidebar }) => {
    const navigate = useNavigate();

    const menuItems = [
        { path: "/", name: "Dashboard", icon: <FaHome /> },
        { path: "/brand", name: "Brand", icon: <FaUsers /> },
        { path: "/getslider", name: "Slider", icon: <TfiLayoutSliderAlt /> },
        { path: "/getproduct", name: "Products", icon: <FaBox /> },
        { path: "/getcategories", name: "Categories", icon: <MdOutlineCategory /> },
        { path: "/brandforms", name: "Brand Forms", icon: <FaUsers /> },
        { path: "/gallery", name: "Gallery", icon: <GrGallery /> },
        { path: "/viewcategorydeal", name: "View Category Deal", icon: <TbCategory2 /> },
        { path: "/contact", name: "Contact Details", icon: <FaEnvelope /> },
    ];

    const handleLogout = () => {
        localStorage.clear();
        navigate('/');
        window?.location?.reload();
    };

    return (
        <Drawer
            variant="permanent"
            open={isOpen}
            PaperProps={{
                component: motion.div,
                variants: sidebarVariants,
                animate: isOpen ? 'open' : 'closed',
                sx: {
                    boxSizing: 'border-box',
                    backgroundColor: '#f0f2f5', 
                    color: '#6b7280', 
                    overflowX: 'hidden', 
                    display: 'flex',
                    flexDirection: 'column',
                    
                },
            }}
            sx={{
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: {
                    width: isOpen ? 300 : 90,
                },
            }}
        >
            <Box sx={{
                padding: 1.5,
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
                minHeight: '64px', 
                scrollBehavior: "smooth",
                position: 'relative',
            }}>
                {/* Logo based on sidebar state */}
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: isOpen ? 'center' : 'center' }}>
                    <img
                        src={isOpen ? "/logo-sociglamm.png" : "/Fevicon_newcolor.png"}
                        alt="Logo"
                        style={{
                            height: isOpen ? 38 : 32,
                            width: isOpen ? 'auto' : 32,
                            transition: 'all 0.3s',
                        }}
                    />
                </Box>
                <IconButton onClick={toggleSidebar} sx={{ color: '#6b7280', position: 'absolute', right: "-10px", zIndex: 999 }}>
                    {isOpen ? <FaChevronLeft /> : <FaChevronRight />}
                </IconButton>
            </Box>

            <motion.div
                variants={listVariants}
                animate={isOpen ? 'open' : 'closed'}
                style={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    scrollbarWidth: 'none', 
                    msOverflowStyle: 'none', 
                }}
                sx={{
                    '&::-webkit-scrollbar': {
                        display: 'none', 
                    },
                }}
            >
                <List>
                    {menuItems.map((item, index) => (
                        <motion.div
                            key={index}
                            variants={listItemVariants}>
                            <ListItem disablePadding sx={{ px: 1, py: 0.5 }}> {/* Added padding for better spacing */}
                                <NavLink
                                    to={item.path}
                                    style={({ isActive }) => ({
                                        textDecoration: 'none',
                                        width: '100%',
                                        color: isActive ? '#ffffff' : '#6b7280', 
                                    })}
                                >
                                    {({ isActive }) => (
                                        <ListItem
                                            button
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%',
                                                gap: isOpen ? 1 : 0,
                                                borderRadius: '8px',
                                                py: 1.2,
                                                px: 2,
                                                my: isActive ? 0 : 1.2,
                                                bgcolor: isActive ? '#00a76f' : 'inherit',
                                                color: isActive ? '#ffffff' : 'inherit',
                                                '&:hover': {
                                                    bgcolor: isActive ? '#00a76f' : '#e5e7eb',
                                                    color: isActive ? '#ffffff' : '#00a76f',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{
                                                minWidth: 'auto',
                                                mr: isOpen ? 2 : 'auto',
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: isActive ? '#ffffff' : 'inherit',
                                            }}>
                                                {item.icon}
                                            </ListItemIcon>
                                            {isOpen && (
                                                <ListItemText
                                                    primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{item.name}</Typography>}
                                                    sx={{ whiteSpace: 'nowrap' }}
                                                />
                                            )}
                                        </ListItem>
                                    )}
                                </NavLink>
                            </ListItem>
                        </motion.div>
                    ))}
                </List>
            </motion.div>

            <Divider sx={{ mx: 2, my: 1 }} /> {/* Add a divider before logout */}

            <Box sx={{ p: 2, pb: 3 }}> {/* Increased bottom padding */}
                <motion.div
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                >
                    <Button
                        variant="contained"
                        startIcon={<FaSignOutAlt />}
                        onClick={handleLogout}
                        sx={{
                            width: '100%',
                            justifyContent: isOpen ? 'flex-start' : 'center',
                            px: 2,
                            py: 1.2,
                            
                            backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                            backdropFilter: 'blur(10px)', 
                            // border: '1px solid rgba(255, 255, 255, 0.2)', 
                            color: '#e33939', 
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', 

                            '&:hover': {
                                backgroundColor: 'rgba(227, 57, 57, 0.7)', 
                                color: '#ffffff',
                            },
                        }}
                    >
                        {isOpen && 'Logout'}
                    </Button>
                </motion.div>
            </Box>
        </Drawer>
    );
};

export default Sidebar;